import { useState } from "react";

import { ArrowRightLeftIcon, BanIcon, EllipsisVerticalIcon, EyeIcon } from "lucide-react";

import { DeleteIcon, PenLineIcon, PublishIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { describeRuleTiers, getFeeConfigVersionLabel } from "../../fee-config.const";
import { FeeConfig, FeeConfigStatusEnum, GroupedFeeConfig } from "../../fee-config.type";
import { getStatusBadge } from "../../pricing-configurator.const";

type FeeConfigCardProps = {
  group: GroupedFeeConfig;
  ratesOnOlderVersions: (config: FeeConfig) => number;
  onView: (config: FeeConfig) => void;
  onEdit: (config: FeeConfig) => void;
  onPublish: (config: FeeConfig) => void;
  onDisable: (config: FeeConfig) => void;
  onDelete: (config: FeeConfig) => void;
  onCreateVersion: (config: FeeConfig) => void;
  onPropagate: (config: FeeConfig) => void;
};

export const FeeConfigCard = ({
  group,
  ratesOnOlderVersions,
  onView,
  onEdit,
  onPublish,
  onDisable,
  onDelete,
  onCreateVersion,
  onPropagate,
}: FeeConfigCardProps) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const displayed = group.allVersions.find((v) => v.id === selectedId) ?? group.current;

  const hasMultipleVersions = group.allVersions.length > 1;
  const badge = getStatusBadge(displayed.status);
  const propagatableCount = displayed.status === FeeConfigStatusEnum.ENABLED ? ratesOnOlderVersions(displayed) : 0;

  const borderColor =
    displayed.status === FeeConfigStatusEnum.ENABLED
      ? "border-l-green-500"
      : displayed.status === FeeConfigStatusEnum.DRAFT
        ? "border-l-amber-400"
        : "border-l-muted-foreground/30";

  return (
    <div
      className={cn(
        "rounded-lg border border-border/80 border-l-[3px] bg-card px-4 py-3 shadow-sm transition-colors",
        borderColor,
      )}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-sm font-semibold truncate leading-snug">{displayed.name}</p>
        <span
          className={cn("rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap shrink-0", badge.className)}
        >
          {badge.label}
        </span>
      </div>

      <p className="text-xs text-muted-foreground mb-2">
        Part client {displayed.customerShare} % · Part ville {displayed.cityShare} %
      </p>

      <div className="space-y-1 mb-3">
        {displayed.rules.map((rule, ruleIndex) => (
          <div key={ruleIndex} className="text-xs text-muted-foreground">
            {displayed.rules.length > 1 && (
              <p className="font-medium text-foreground/80">
                Règle {ruleIndex + 1}
                {rule.customerShare !== undefined && ` (part client ${rule.customerShare} %)`}
              </p>
            )}
            {describeRuleTiers(rule).map((line, lineIndex) => (
              <p key={lineIndex}>{line}</p>
            ))}
          </div>
        ))}
      </div>

      {displayed.publishedAt && (
        <p className="text-xs text-muted-foreground mb-3">
          Publiée le {new Date(displayed.publishedAt).toLocaleDateString("fr-FR")}
        </p>
      )}

      <div className="flex items-center justify-between gap-2">
        {hasMultipleVersions ? (
          <Select value={displayed.id} onValueChange={setSelectedId}>
            <SelectTrigger size="sm" className="cursor-pointer text-xs max-w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {group.currentVersions.length > 0 && (
                <SelectGroup>
                  <SelectLabel>Version courante</SelectLabel>
                  {group.currentVersions.map((v) => (
                    <SelectItem key={v.id} value={v.id} className="cursor-pointer text-xs">
                      {getFeeConfigVersionLabel(v)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
              {group.previousVersions.length > 0 && (
                <SelectGroup>
                  <SelectLabel>Désactivé</SelectLabel>
                  {group.previousVersions.map((v) => (
                    <SelectItem key={v.id} value={v.id} className="cursor-pointer text-xs">
                      {getFeeConfigVersionLabel(v)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
            </SelectContent>
          </Select>
        ) : (
          <div />
        )}

        <div className="flex items-center gap-2 justify-end shrink-0">
          {displayed.status === FeeConfigStatusEnum.DRAFT && (
            <Button size="sm" className="cursor-pointer gap-2" onClick={() => onPublish(displayed)}>
              <PublishIcon className="h-4 w-4" color="#FFFFFF" />
              Publier
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 cursor-pointer">
                <EllipsisVerticalIcon className="h-4 w-4" />
                <span className="sr-only">Actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {displayed.status === FeeConfigStatusEnum.DRAFT ? (
                <>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => onEdit(displayed)}>
                    <PenLineIcon className="h-4 w-4" />
                    Modifier
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    variant="destructive"
                    onClick={() => onDelete(displayed)}
                  >
                    <DeleteIcon className="h-4 w-4" />
                    Supprimer
                  </DropdownMenuItem>
                </>
              ) : (
                <>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => onView(displayed)}>
                    <EyeIcon className="h-4 w-4" />
                    Voir
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => onCreateVersion(displayed)}>
                    <PenLineIcon className="h-4 w-4" />
                    Nouvelle version
                  </DropdownMenuItem>
                  {propagatableCount > 0 && (
                    <DropdownMenuItem className="cursor-pointer" onClick={() => onPropagate(displayed)}>
                      <ArrowRightLeftIcon className="h-4 w-4" />
                      Propager ({propagatableCount} tarification{propagatableCount > 1 ? "s" : ""})
                    </DropdownMenuItem>
                  )}
                  {displayed.status === FeeConfigStatusEnum.ENABLED && (
                    <DropdownMenuItem
                      className="cursor-pointer"
                      variant="destructive"
                      onClick={() => onDisable(displayed)}
                    >
                      <BanIcon className="h-4 w-4" />
                      Désactiver
                    </DropdownMenuItem>
                  )}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};
